import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import {
  changeStaffMemberStatus,
  deleteStaffMember,
  STAFF_STATUSES,
} from '../../../services/staffDirectoryService'

const ACTION_META = {
  status: { title: 'Change status', tone: 'primary' },
  delete: {
    title: 'Remove staff member',
    description: 'Remove this staff member from the directory? Their record is archived (soft-deleted) and can be restored by support.',
    confirmLabel: 'Remove',
    tone: 'danger',
  },
}

export default function StaffActionsModal({ isOpen, onClose, action, member, onSuccess }) {
  const [status, setStatus] = useState('active')
  const [working, setWorking] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState('')

  useEffect(() => {
    if (!isOpen || !member) return
    setStatus(member.status || 'active')
    setError('')
    setDone('')
    setWorking(false)
  }, [isOpen, member])

  if (!isOpen || !member) return null
  const meta = ACTION_META[action] || {}

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    setWorking(true)
    setError('')
    try {
      if (action === 'status') {
        await changeStaffMemberStatus(member.id, status)
        setDone('Staff member status updated.')
      } else if (action === 'delete') {
        await deleteStaffMember(member.id)
        setDone('Staff member removed.')
      }
      onSuccess?.()
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to complete the action.')
    } finally {
      setWorking(false)
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-gray-900/50"
      />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-bold text-gray-900">{meta.title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>

        {error ? (
          <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {done ? (
          <>
            <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {done}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full rounded-lg bg-gray-900 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-gray-800"
            >
              Done
            </button>
          </>
        ) : action === 'status' ? (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <div className="rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-sm font-bold text-gray-900">{member.name}</p>
              <p className="text-xs text-gray-500">{member.employeeId}</p>
            </div>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Status</span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-rapid-orange/30"
              >
                {STAFF_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </label>
            <div className="flex items-center justify-end gap-3 pt-1">
              <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100">
                Cancel
              </button>
              <button
                type="submit"
                disabled={working}
                className="rounded-lg bg-rapid-orange px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-[#E84A15] disabled:opacity-60"
              >
                {working ? 'Saving…' : 'Save status'}
              </button>
            </div>
          </form>
        ) : (
          <>
            <p className="mt-3 text-sm text-gray-600">{meta.description}</p>
            <div className="mt-4 rounded-lg bg-gray-50 px-4 py-3">
              <p className="text-sm font-bold text-gray-900">{member.name}</p>
              <p className="text-xs text-gray-500">{member.employeeId}</p>
            </div>
            <div className="mt-5 flex items-center justify-end gap-3">
              <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-100">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleSubmit()}
                disabled={working}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
              >
                {working ? 'Working…' : meta.confirmLabel}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  )
}
